import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import { useUser } from "../contexts/UserContext";
import WelcomeHeader from "../components/WelcomeHeader";
import SupportHelpSection from "./SupportHelpSection";

import DashboardLegacyMessages from "./DashboardLegacyMessages";
import DashboardDeliveredMessages from "./DashboardDeliveredMessages";
import DashboardRecipients from "./DashboardRecipients";
import DashboardFiles from "./DashboardFiles";
import DashboardInheritedMessages from "./DashboardInheritedMessages";

import "../styleSheets/dashboard.css";

import { queryKeys } from "../graphql/queryKeys";
import {
  fetchLegacyMessagesByUserId,
  fetchRecipientsByUserId,
  fetchAttachmentsByUserId,
  fetchDeliveredMessagesByRecipientEmail,
  fetchDeliveredMessagesBySender,
  fetchUserPlanByUserId,
} from "../graphql/fetchers";
import { BillingPlan } from "../utilities/billing";
import { logToConsole } from "../utilities/utilities";
import BlockingPageLoader from "./ui/BlockingPageLoader";

function Dashboard() {
  const { user, isAuthenticated } = useUser();
  const queryClient = useQueryClient();

  const userId = user?.userId ?? "";
  const loggedInEmail = (user?.signInDetails?.loginId ?? "")
    .trim()
    .toLowerCase();
  const navigate = useNavigate();
  const { t } = useTranslation("common");

  const alertShownRef = useRef(false);
  const [isInitialDashboardLoading, setIsInitialDashboardLoading] =
    useState(true);

  useEffect(() => {
    if (!isAuthenticated && !alertShownRef.current) {
      navigate("/", {
        state: { message: "dashboard's " + t("common.signIn") },
      });
      alertShownRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  // ✅ Prefetch everything ASAP so other components/tabs can reuse cache without refetching
  useEffect(() => {
    if (!isAuthenticated || !userId) return;

    let isMounted = true;

    setIsInitialDashboardLoading(true);

    logToConsole("log", "Prefetching for userId:", userId);
    const prefetches: Promise<unknown>[] = [
      queryClient.prefetchQuery<BillingPlan | null>({
        queryKey: queryKeys.plan(userId),
        queryFn: () => fetchUserPlanByUserId(userId),
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.legacyMessages(userId),
        queryFn: () => fetchLegacyMessagesByUserId(userId),
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.recipients(userId),
        queryFn: () => fetchRecipientsByUserId(userId),
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.attachments(userId),
        queryFn: () => fetchAttachmentsByUserId(userId),
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.deliveredMessagesBySender(userId),
        queryFn: () => fetchDeliveredMessagesBySender(userId),
      }),
    ];

    logToConsole("log", "Dashboard prefetching loggedInEmail:", loggedInEmail);
    if (loggedInEmail) {
      prefetches.push(
        queryClient.prefetchQuery({
          queryKey: queryKeys.deliveredMessages(loggedInEmail),
          queryFn: () => fetchDeliveredMessagesByRecipientEmail(loggedInEmail),
        }),
      );
    }

    void Promise.allSettled(prefetches).finally(() => {
      if (isMounted) {
        setIsInitialDashboardLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, userId, loggedInEmail, queryClient]);

  // If not signed in, redirect
  if (!isAuthenticated) {
    return <Navigate to="/" replace state={{ toast: { type: "info" } }} />;
  }

  if (isInitialDashboardLoading) {
    return <BlockingPageLoader message={t("dashboard.loadingData")} />;
  }

  return (
    <>
      <section className={"section-dashboard"} aria-labelledby="dashboard-page-heading">
        <section className={"dashboard-header"}>
          <WelcomeHeader variant={"dashboard"} id="dashboard-page-heading" />
        </section>

        <section className={"dashboard-container-left"}>
          <DashboardLegacyMessages />
          <DashboardDeliveredMessages />
        </section>

        <section className={"dashboard-container-right"}>
          <DashboardRecipients />
          <DashboardFiles />
          <DashboardInheritedMessages />
        </section>
        <section className="dashboard-container-bottom">
          <SupportHelpSection />
        </section>
      </section>
    </>
  );
}

export default Dashboard;
